import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Camera, Search, SearchX, CloudUpload } from "lucide-react";
import notification from "../../../../helpers/notification";
import { TspBreadcrumb, TspConfirmDialog, TspEmptyState, TspSkeleton, TspTabs } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import useTspPermission from "../../hooks/useTspPermission";
import { tspErrorMessage } from "../../hooks/useTspApiResource";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import useTspJourneyLocationOptions from "../../hooks/useTspJourneyLocationOptions";
import useTspMediaList, { removeMedia, uploadMedia } from "./useTspMediaData";
import { MEDIA_TYPE_TABS, TAB_TO_COUNT } from "./mediaModel";
import MediaCard from "./components/MediaCard";
import MediaViewerModal from "./components/MediaViewerModal";
import MediaUploadModal from "./components/MediaUploadModal";

const SEARCH_DEBOUNCE_MS = 300;

// Media Gallery (/tsp/media-gallery). Journey/city filters can be preset from
// the URL (?journey=<id>&location_id=<id>) so other screens can deep-link into
// it. Every filter, the type tabs' counts and the paging are done by the server.
export default function TspMediaGalleryPage() {
  const { hasPermission } = useTspPermission();
  const [searchParams] = useSearchParams();

  // No separate media-manage key exists in the approved permission set: removing
  // media uses the upload permission (the server enforces it and journey scope).
  const canManageMedia = hasPermission(TSP_PERMISSIONS.MEDIA_UPLOAD);
  const canOpenKnowledge = hasPermission(TSP_PERMISSIONS.KNOWLEDGE_REPOSITORY_VIEW);

  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState(() => ({ search: "", journeyId: searchParams.get("journey") || "", locationId: searchParams.get("location_id") || "", tab: "All" }));
  const [viewerId, setViewerId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const media = useTspMediaList(filters);
  const options = useTspJourneyLocationOptions();

  useEffect(() => {
    const timer = setTimeout(() => setFilters((previous) => (previous.search === searchInput ? previous : { ...previous, search: searchInput })), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const setFilter = (key, value) => setFilters((previous) => ({ ...previous, [key]: value }));
  const clearFilters = () => {
    setSearchInput("");
    setFilters({ search: "", journeyId: "", locationId: "", tab: "All" });
  };
  const hasActiveFilters = Boolean(filters.search || filters.journeyId || filters.locationId || filters.tab !== "All");

  const { items } = media;
  const viewerIndex = viewerId ? items.findIndex((entry) => entry.id === viewerId) : -1;
  const locationOptions = options.locations.some((entry) => String(entry.id) === filters.locationId) || !filters.locationId ? options.locations : [{ id: filters.locationId, label: "Selected city" }, ...options.locations];

  const handleUpload = async (values, file) => {
    await uploadMedia(values, file); // rejects with the server's reason; the dialog shows it
    notification("success", `“${values.caption.trim()}” uploaded.`);
    setUploadOpen(false);
    clearFilters(); // make sure the new item is visible
    media.reload();
  };

  const handleRemoveConfirmed = async () => {
    setRemoving(true);
    try {
      await removeMedia(removeTarget.id);
      notification("success", `“${removeTarget.caption}” removed.`);
      setRemoveTarget(null);
      setViewerId(null);
      media.reload();
    } catch (error) {
      notification("error", tspErrorMessage(error, "Could not remove this media. Please try again."));
    } finally {
      setRemoving(false);
    }
  };

  // Only the very first load (or a first load that failed) replaces the whole page;
  // after that the filter bar stays mounted so typing in the search box is never
  // interrupted by a reload of the results.
  const firstLoad = !media.meta;

  const heading = (
    <div className="tsp-page-heading-row">
      <div>
        <h1>Media Gallery</h1>
        <TspBreadcrumb items={["Dashboard", "Media Gallery"]} />
      </div>
      {canManageMedia && !(firstLoad && (media.loading || media.error)) && (
        <button type="button" className="tsp-btn tsp-btn-success tsp-btn-pill" onClick={() => setUploadOpen(true)}>
          <CloudUpload size={15} /> Upload Media
        </button>
      )}
    </div>
  );

  if (firstLoad && media.loading) {
    return (
      <div className="tsp-page tsp-media-page">
        {heading}
        <TspSkeleton count={8} label="Loading media" />
      </div>
    );
  }
  if (firstLoad && media.error) {
    return (
      <div className="tsp-page tsp-media-page">
        {heading}
        <TspLoadError error={media.error} onRetry={media.reload} what="the media gallery" />
      </div>
    );
  }

  const nothingAtAll = !media.loading && !media.error && !hasActiveFilters && media.counts.all === 0;

  return (
    <div className="tsp-page tsp-media-page">
      {heading}

      {nothingAtAll ? (
        <TspEmptyState
          icon={Camera}
          title="No media yet"
          message="Photos and videos from journeys will appear here once they are uploaded."
          action={
            canManageMedia && (
              <button type="button" className="tsp-btn tsp-btn-success" onClick={() => setUploadOpen(true)}>
                <CloudUpload size={15} /> Upload Media
              </button>
            )
          }
        />
      ) : (
        <>
          <div className="tsp-journey-filters" role="search">
            <label className="tsp-journey-search">
              <Search size={16} aria-hidden="true" />
              <input type="search" placeholder="Search caption or city…" aria-label="Search media" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
            </label>
            <select className="tsp-select tsp-filter-select" aria-label="Filter by journey" value={filters.journeyId} onChange={(event) => setFilter("journeyId", event.target.value)}>
              <option value="">All journeys</option>
              {options.journeys.map((journey) => (
                <option key={journey.id} value={journey.id}>
                  {journey.journey_name}
                </option>
              ))}
            </select>
            <select className="tsp-select tsp-filter-select" aria-label="Filter by city" value={filters.locationId} onChange={(event) => setFilter("locationId", event.target.value)}>
              <option value="">All cities</option>
              {locationOptions.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.label}
                </option>
              ))}
            </select>
            {hasActiveFilters && (
              <button type="button" className="tsp-btn tsp-btn-outline tsp-btn-lg" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>

          <div className="tsp-results-bar">
            <TspTabs
              className="tsp-tabs-compact"
              tabs={MEDIA_TYPE_TABS.map((tab) => `${tab} (${media.counts[TAB_TO_COUNT[tab]]})`)}
              active={`${filters.tab} (${media.counts[TAB_TO_COUNT[filters.tab]]})`}
              onChange={(label) => setFilter("tab", label.replace(/ \(\d+\)$/, ""))}
            />
            <span className="tsp-muted-text" aria-live="polite">
              Showing {items.length} of {media.meta ? media.meta.total : items.length} {media.meta?.total === 1 ? "item" : "items"}
            </span>
          </div>

          {media.loading ? (
            <TspSkeleton count={8} label="Loading media" />
          ) : media.error ? (
            <TspLoadError error={media.error} onRetry={media.reload} what="the media gallery" />
          ) : items.length === 0 ? (
            <TspEmptyState
              icon={SearchX}
              title={filters.locationId && !filters.search && !filters.journeyId && filters.tab === "All" ? "No media available for this city." : "No media matches your filters"}
              message="Try a different search, or clear the filters to see everything."
              action={
                <button type="button" className="tsp-btn tsp-btn-outline-primary" onClick={clearFilters}>
                  Clear filters
                </button>
              }
            />
          ) : (
            <>
              <div className="tsp-media-card-grid">
                {items.map((entry) => (
                  <MediaCard key={entry.id} item={entry} canRemove={canManageMedia} canOpenKnowledge={canOpenKnowledge} onView={setViewerId} onRemove={setRemoveTarget} />
                ))}
              </div>
              {media.remaining > 0 && (
                <div className="tsp-load-more">
                  <button type="button" className="tsp-btn tsp-btn-outline-primary" onClick={media.loadMore} disabled={media.loadingMore}>
                    {media.loadingMore ? "Loading…" : `Load more (${media.remaining} remaining)`}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {viewerIndex >= 0 && (
        <MediaViewerModal
          items={items}
          index={viewerIndex}
          onIndexChange={(next) => setViewerId(items[next].id)}
          onClose={() => setViewerId(null)}
          canRemove={canManageMedia}
          canOpenKnowledge={canOpenKnowledge}
          onRemove={(entry) => {
            setViewerId(null); // never stack two dialogs
            setRemoveTarget(entry);
          }}
        />
      )}

      {uploadOpen && <MediaUploadModal onClose={() => setUploadOpen(false)} onSubmit={handleUpload} />}

      <TspConfirmDialog
        open={Boolean(removeTarget)}
        title="Remove this media?"
        message={removeTarget ? `“${removeTarget.caption}” will be permanently removed.` : ""}
        confirmLabel="Remove"
        busy={removing}
        onConfirm={handleRemoveConfirmed}
        onCancel={() => setRemoveTarget(null)}
      />

      <ToastContainer />
    </div>
  );
}
